import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'

const RECENTLY_VIEWED_PREFIX = 'mryusuf_recently_viewed_'
const MAX_ITEMS = 10

/**
 * Hook to manage recently viewed products
 * Uses localStorage for persistence, scoped per user
 */
export function useRecentlyViewed() {
  const { user, loading: authLoading } = useAuth()
  const [recentlyViewedIds, setRecentlyViewedIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  // Get the storage key based on user (or 'guest' for non-logged-in users)
  const getStorageKey = useCallback(() => {
    return `${RECENTLY_VIEWED_PREFIX}${user?.id || 'guest'}`
  }, [user?.id])

  // Load from localStorage when user changes (only after auth is stable)
  useEffect(() => {
    // Don't run while auth is still loading - wait for stable user state
    if (authLoading) return

    setLoading(true)
    try {
      const storageKey = getStorageKey()
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          // Filter to ensure all items are strings (product IDs)
          setRecentlyViewedIds(parsed.filter((id): id is string => typeof id === 'string'))
        } else {
          setRecentlyViewedIds([])
        }
      } else {
        setRecentlyViewedIds([])
      }
    } catch {
      setRecentlyViewedIds([])
    }
    setLoading(false)
  }, [getStorageKey, authLoading])

  // Persist to localStorage whenever list changes
  useEffect(() => {
    // Don't persist while auth is loading or initial load incomplete
    if (authLoading || loading) return

    const storageKey = getStorageKey()
    if (recentlyViewedIds.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(recentlyViewedIds))
    } else {
      localStorage.removeItem(storageKey)
    }
  }, [recentlyViewedIds, loading, getStorageKey, authLoading])

  /**
   * Add a product ID to the recently viewed list
   * - Moves to front if already exists
   * - Keeps only the most recent MAX_ITEMS
   */
  const addToRecentlyViewed = useCallback((productId: string) => {
    if (!productId) return

    setRecentlyViewedIds(prev => {
      // Remove the product if it already exists (to move it to front)
      const filtered = prev.filter(id => id !== productId)
      // Add to front and limit to MAX_ITEMS
      return [productId, ...filtered].slice(0, MAX_ITEMS)
    })
  }, [])

  /**
   * Clear all recently viewed products
   */
  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewedIds([])
    localStorage.removeItem(getStorageKey())
  }, [getStorageKey])

  /**
   * Get recently viewed IDs excluding a specific product
   * Useful when displaying on a product page to exclude the current product
   */
  const getRecentlyViewedExcluding = useCallback((excludeId: string) => {
    return recentlyViewedIds.filter(id => id !== excludeId)
  }, [recentlyViewedIds])

  return {
    recentlyViewedIds,
    loading: loading || authLoading,  // Include auth loading in returned state
    addToRecentlyViewed,
    clearRecentlyViewed,
    getRecentlyViewedExcluding
  }
}
